/**
 * Error reporting without Sentry in the entry chunk (ADR-WES1).
 *
 * `startErrorReporting` (main.tsx) does nothing without a DSN, exactly like
 * the old inline init. With a DSN it listens for uncaught errors and unhandled
 * rejections straight away, keeps at most `MAX_BUFFERED` of them in memory,
 * and loads `./sentry-client` at idle time after the page's load event (so it
 * never competes with the first screen's downloads), or immediately when the
 * first error arrives. When Sentry is up,
 * the buffered errors are replayed through `captureException` (so they pass
 * the same `beforeSend` scrubber) and the early listeners are removed: Sentry's
 * own global handlers take over. Nothing is written to storage.
 *
 * `reportError` is what components call (StitchErrorBoundary); before Sentry is
 * up it buffers the same way.
 */
type Captured = {
  error: unknown;
  hint?: { tags?: Record<string, string>; extra?: Record<string, unknown> };
  mechanism?: "onerror" | "onunhandledrejection";
};

type CaptureHint =
  | { tags?: Record<string, string>; extra?: Record<string, unknown> }
  | { mechanism: { type: string; handled: boolean } };

type SentryApi = {
  captureException(exception: unknown, hint?: CaptureHint): string;
};

export const MAX_BUFFERED = 20;

let enabled = false;
let sentry: SentryApi | null = null;
let loading: Promise<void> | null = null;
let loadSentry: (() => Promise<SentryApi>) | null = null;
const buffered: Captured[] = [];
let detachEarlyListeners: (() => void) | null = null;

function capture(sdk: SentryApi, item: Captured) {
  if (item.mechanism) {
    // Same shape Sentry's own global handlers give uncaught errors.
    sdk.captureException(item.error, { mechanism: { type: item.mechanism, handled: false } });
  } else {
    sdk.captureException(item.error, item.hint);
  }
}

function buffer(item: Captured) {
  if (buffered.length < MAX_BUFFERED) buffered.push(item);
  // Do not wait for idle once something went wrong.
  void ensureSentry();
}

function ensureSentry(): Promise<void> {
  if (!enabled || !loadSentry) return Promise.resolve();
  loading ??= loadSentry().then(
    (sdk) => {
      detachEarlyListeners?.();
      detachEarlyListeners = null;
      sentry = sdk;
      for (const item of buffered.splice(0)) capture(sdk, item);
    },
    (error: unknown) => {
      // Reporting must never break the app; a later error retries the load.
      loading = null;
      console.error("Failed to load error reporting", error);
    }
  );
  return loading;
}

/** Report a caught error (e.g. from an error boundary). */
export function reportError(error: unknown, hint?: Captured["hint"]): void {
  if (sentry) {
    sentry.captureException(error, hint);
    return;
  }
  if (!enabled) return;
  buffer({ error, hint });
}

type StartOptions = {
  dsn: string | undefined;
  environment: string | undefined;
  /** Test seam; defaults to the lazy `./sentry-client` chunk. */
  load?: (dsn: string, environment: string | undefined) => Promise<SentryApi>;
  /** Test seam; defaults to `scheduleAfterPageLoad`. */
  schedule?: (callback: () => void) => void;
};

/**
 * Idle time after the page's load event. An idle callback alone can run while
 * the first screen's fonts, images and chunks are still downloading (the main
 * thread idles while it waits for them), and the Sentry chunk would then
 * compete with them for bandwidth on a slow connection. An error before then
 * still loads Sentry at once (`buffer`).
 */
export function scheduleAfterPageLoad(callback: () => void): void {
  const idle = () => {
    if (typeof window.requestIdleCallback === "function") {
      window.requestIdleCallback(callback, { timeout: 4_000 });
    } else {
      window.setTimeout(callback, 1_500);
    }
  };
  if (document.readyState === "complete") idle();
  else window.addEventListener("load", idle, { once: true });
}

export function startErrorReporting({ dsn, environment, load, schedule }: StartOptions): void {
  if (!dsn || enabled || typeof window === "undefined") return;
  enabled = true;
  loadSentry = () =>
    load
      ? load(dsn, environment)
      : import("./sentry-client").then(({ initSentry }) => initSentry(dsn, environment));
  const onError = (event: ErrorEvent) => {
    buffer({ error: event.error ?? event.message, mechanism: "onerror" });
  };
  const onRejection = (event: PromiseRejectionEvent) => {
    buffer({ error: event.reason, mechanism: "onunhandledrejection" });
  };
  window.addEventListener("error", onError);
  window.addEventListener("unhandledrejection", onRejection);
  detachEarlyListeners = () => {
    window.removeEventListener("error", onError);
    window.removeEventListener("unhandledrejection", onRejection);
  };
  (schedule ?? scheduleAfterPageLoad)(() => void ensureSentry());
}

/** Test seam: forget all state. */
export function resetErrorReportingForTests(): void {
  detachEarlyListeners?.();
  detachEarlyListeners = null;
  enabled = false;
  sentry = null;
  loading = null;
  loadSentry = null;
  buffered.splice(0);
}
